import {
  Description as DocumentIcon,
  GetApp as DownloadIcon,
  Folder as FolderIcon,
  Image as ImageIcon,
  PlayArrow as PlayIcon,
  VideoFile as VideoIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  IconButton,
  Typography,
} from '@mui/material';
import React from 'react';

import { FileItem } from '../Api';

interface FileCardProps {
  fileItem: FileItem;
  onTranscode: (fileItem: FileItem) => void;
}

const FileCard: React.FC<FileCardProps> = ({ fileItem, onTranscode }) => {
  const getFileIcon = (filename: string) => {
    const extension = filename.toLowerCase().split('.').pop();
    
    switch (extension) {
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
      case 'flv':
        return <VideoIcon />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return <ImageIcon />;
      case 'pdf':
      case 'doc':
      case 'docx':
      case 'txt':
        return <DocumentIcon />;
      default:
        return <FolderIcon />;
    }
  };

  const getFileTypeColor = (filename: string) => {
    const extension = filename.toLowerCase().split('.').pop();
    
    switch (extension) {
      case 'mp4':
      case 'avi':
      case 'mov':
      case 'wmv':
      case 'flv':
        return 'error';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return 'success';
      case 'pdf':
      case 'doc':
      case 'docx':
      case 'txt':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No date';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatUploadDate = (uploadTimestamp: string) => {
    try {
      const date = new Date(+uploadTimestamp);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch {
      return 'Unknown';
    }
  };

  const displayTitle = fileItem.title || fileItem.filename;
  const fileExtension = fileItem.filename.toLowerCase().split('.').pop();

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: `${getFileTypeColor(fileItem.filename)}.main`,
              mr: 2,
              width: 48,
              height: 48,
            }}
          >
            {getFileIcon(fileItem.filename)}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              component="h3"
              sx={{
                fontSize: '1.1rem',
                fontWeight: 600,
                mb: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {displayTitle}
            </Typography>
            <Chip
              label={fileExtension?.toUpperCase() || 'FILE'}
              size="small"
              color={getFileTypeColor(fileItem.filename) as any}
              sx={{ mb: 1 }}
            />
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Date:</strong> {formatDate(fileItem.date)}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Size:</strong> {fileItem.size}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Uploaded:</strong> {formatUploadDate(fileItem.upload)}
          </Typography>
        </Box>
      </CardContent>

      <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          {fileItem.signedUrl ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayIcon />}
              onClick={() => window.location.href = `video/${fileItem.key}`}
              size="small"
              fullWidth
            >
              Play
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => onTranscode(fileItem)}
              size="small"
              fullWidth
            >
              Transcode
            </Button>
          )}
          <IconButton
            color="primary"
            size="small"
            title="Download"
            sx={{ flexShrink: 0 }}
          >
            <DownloadIcon />
          </IconButton>
        </Box>
      </CardActions>
    </Card>
  );
};

export default FileCard;
